import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import {
  chain, cloneDeep, forEach, get, includes, isArray, isEmpty,
  isNumber, isString, map, reduce, some, trim,
} from 'lodash';
import { mapState } from 'pinia';
import { uid } from 'quasar';
import { notifySuccess } from 'src/composables/interactions';
import { auth } from 'src/store/auth';
import { region } from 'src/store/region';
import Arabic from 'src/utils/Arabic';
import { findFreeFileNameOnNativeApp, getPdfBase64 } from 'src/utils/ExportUtils';
import {
  calcMergedCellWidth as calcMergedCellWidthUtil,
  fixElementMargin as fixElementMarginUtil,
  fixMergedCells as fixMergedCellsUtil,
  getImageDimensions as getImageDimensionsUtil,
  removePdfTableBorders as removePdfTableBordersUtil,
} from 'src/utils/PdfMakeUtils';

import Toolbox from './Toolbox';

const WIDTH_OF_TABLE_BORDER = 0.0175;
const TABLE_COLS_DEFAULT_QUANTITY = 3;
const INITIAL_MULTIPLIER_VALUE = 1;
const MINIMUM_CELL_WIDTH = 1;
/**
 * Multiplier we use to fit table in pdf page size
 */
const PDF_FACTOR = 0.635;
/**
 * Value represents the fill color opacity of the merged vertically table cell containing image
 * so that image won't be covered by fill color
 */
const MERGED_HEADER_CELL_FILL_OPACITY = 0.07;
const SIGNATURE_ELEMENT_WIDTH = 200;

export default {
  mixins: [Toolbox],

  data() {
    return {
      pdfMakePageSettings: {
        maxPageWidth: 550,
        maxPageHeight: 800,
        pagePaddingsX: 45,
      },

      pdfFactor: PDF_FACTOR,

      reportElementPathsToRenderedText: [
        'credentials',
        'userName',
        'heading',
        'textContent',
        'chartHeaderText',
        'chartFooterText',
      ],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(region, ['region']),

    isNativeIos() {
      return this.$q.platform.is.capacitor && this.$q.platform.is.ios;
    }, // isNativeIos()

    isNativeAndroid() {
      return this.$q.platform.is.capacitor && this.$q.platform.is.android;
    }, // isNativeIos()

    pdfMakeDefaultPageSize() {
      // Letter countries: US, Canada, Philipines. We currently just use letter for US and Canada regions.
      return ['us', 'ca'].includes(this.region) ? 'LETTER' : 'A4';
    }, // pdfMakeDefaultPageSize()

    pdfMakeDefaultStyles() {
      return {
        img: {
          maxWidth: this.pdfMakePageSettings.maxPageWidth,
          maxHeight: this.pdfMakePageSettings.maxPageHeight,
          margin: [0, 12, 0, 12],
        },
        table: {
          width: this.pdfMakePageSettings.maxPageWidth,
        },
        p: {
          margin: [0, 0, 0, 0],
        },
      };
    }, // pdfMakeDefaultStyles()

    isUserLanguageArabic() {
      return this.userDetails.language === 'ar';
    }, // isUserLanguageArabic()

    signatureElementMargin() {
      return [0, 0, (this.pdfMakePageSettings.maxPageWidth - SIGNATURE_ELEMENT_WIDTH), 0];
    },

    pdfMakeFont() {
      return this.userDetails.language === 'ar' ? 'Cairo' : 'PTSerif';
    }, // pdfMakeFont()
  },

  methods: {

    /**
     * @param {Object[]} reportElements
     */
    isArabicContainedInReportElements(reportElements) {
      if (isEmpty(reportElements)) {
        return false;
      }

      return some(reportElements, (element) => {
        return some(this.reportElementPathsToRenderedText, (field) => {
          const text = get(element, field, '');
          return Arabic.isContainedInString(text);
        });
      });
    }, // isArabicContainedInReportElements()

    /**
     * Clears HTML string from inline row breaks and some inline styles
     *
     * @param {string} html
     * @return {string}
     */
    clearUnnecessaryHtmlParts(html) {
      const updatedHtml = html
        .replace(/(\r\n|\n\r|\r|\n)/g, '<br />')
        // replace inline "width" style with any string property
        .replace(/width:(.)?[a-zA-Z-]+;?/g, '')
        .replace(
          /font-family:(.*?)([;"])/g,
          (match, font, lastSign) => (lastSign === '"' ? lastSign : ''),
        ) // replace custom inline fonts
        .replace(
          /text-decoration: none([;"])/g,
          (match, lastSign) => (lastSign === '"' ? lastSign : ''),
        );

      return this.fixMergedCells(updatedHtml);
    },

    /**
     * Fixes case when users merge all cells in each column to achieve 1 row tables.
     *
     * @param {string} html
     * @return {string}
     */
    fixMergedCells(html) {
      return fixMergedCellsUtil(html);
    }, // fixMergedCells()

    /**
     * Calculates pdf element table cell auto width
     *
     * @param {(number | '*')[]} tableColsWidths - defines a width of each column in table
     * @return {number}
     */
    calcCellAutoWidth(tableColsWidths) {
      const { maxPageWidth, pagePaddingsX } = this.pdfMakePageSettings;
      let colsWithoutCustomWidth = 0;

      const totalCustomWidth = reduce(tableColsWidths, (acc, width) => {
        const isCustomWidth = width !== '*' && isNumber(width);
        if (isCustomWidth) {
          return acc + width;
        }

        colsWithoutCustomWidth += 1;
        return acc;
      }, 0);

      if (!colsWithoutCustomWidth) {
        return MINIMUM_CELL_WIDTH;
      }

      const tableBordersWidthSubtractionMultiplier = INITIAL_MULTIPLIER_VALUE
        - (WIDTH_OF_TABLE_BORDER * (tableColsWidths.length - TABLE_COLS_DEFAULT_QUANTITY));

      const calculatedCellWidth = ((maxPageWidth - pagePaddingsX - totalCustomWidth) / colsWithoutCustomWidth)
        * tableBordersWidthSubtractionMultiplier;
      // Cell width should have some value and not be negative
      return calculatedCellWidth <= 0 ? MINIMUM_CELL_WIDTH : calculatedCellWidth;
    },

    /**
     * Calculate merged cell width.
     * Return cell auto-width if it isn't merged.
     *
     * @param {object} cell
     * @param {(number | '*')[]} tableWidths
     * @param {number} cellAutoWidth
     * @param {number} colIdx
     * @return {number}
     */
    calcMergedCellWidth(cell, tableWidths, cellAutoWidth, colIdx) {
      return calcMergedCellWidthUtil(cell, tableWidths, cellAutoWidth, colIdx);
    }, // calcMergedCellWidth()

    /**
     * Changing content/images references names to make them unique
     *
     * @param {object} content
     * @param {object} images
     * @param {number | null} index
     * @return {{content: object[], images: object}}
     */
    changeImagesReferencesNames(content, images, index) {
      const identificator = chain(get(images, 'img_ref_0'), `token=${uid()}`)
        .split('token=')
        .last()
        .value();

      return {
        content: map(
          content,
          (pdfPageContent) => this.getFixedPdfPageContent(
            pdfPageContent,
            'chartHeader',
            `${index}-${identificator}`,
          ),
        ),

        images: reduce(images, (acc, value, key) => {
          acc[`${key}-chartHeader-${index}-${identificator}`] = value;
          return acc;
        }, {}),
      };
    }, // changeImagesReferencesNames()

    /**
     * Fixes pdfPage.content image reference name and images width within table and tables formatting
     *
     * @param {Object} pdfPageContent
     * @param {string} keyWord
     * @param {number} index
     * @returns {Object} new pdfPage.content object
     */
    getFixedPdfPageContent(pdfPageContent, keyWord, index) {
      if (!pdfPageContent) {
        return pdfPageContent;
      }

      let pdfPageContentCopy = cloneDeep(pdfPageContent);

      // if elem has text and text has Arabic language change alignment
      if ((get(pdfPageContentCopy, 'text') && Arabic.isPrevailInString(this.getTextFromNode(pdfPageContentCopy)))) {
        pdfPageContentCopy.direction = 'rtl';
      }

      if ((get(pdfPageContentCopy, 'nodeName') === 'P') && (get(pdfPageContentCopy, 'text') === '')) {
        pdfPageContentCopy.text = ' ';
      }
      // if elem is <img>
      if (get(pdfPageContentCopy, 'nodeName') === 'IMG') {
        pdfPageContentCopy.image += `-${keyWord}-${index}`;
        pdfPageContentCopy.width *= PDF_FACTOR;
        return pdfPageContentCopy;
      }

      if (get(pdfPageContentCopy, 'stack') && !isEmpty(pdfPageContentCopy.stack)) {
        pdfPageContentCopy.stack = map(
          pdfPageContentCopy.stack,
          (stackItem) => this.getFixedPdfPageContent(stackItem, keyWord, index),
        );
      }

      // if elem is <ul>
      if (get(pdfPageContentCopy, 'ul') && isArray(pdfPageContentCopy.ul) && pdfPageContentCopy.ul.length) {
        pdfPageContentCopy.ul = map(pdfPageContentCopy.ul, (ul) => {
          return this.getFixedPdfPageContent(ul, keyWord, index);
        });
      }

      // if elem is <ol>
      if (get(pdfPageContentCopy, 'ol') && isArray(pdfPageContentCopy.ol) && pdfPageContentCopy.ol.length) {
        pdfPageContentCopy.ol = map(pdfPageContentCopy.ol, (ol) => {
          return this.getFixedPdfPageContent(ol, keyWord, index);
        });
      }

      // fix all tables widths and content
      const isElementTable = pdfPageContentCopy.table && (typeof pdfPageContentCopy.table === 'object');
      const isElemProperTable = isElementTable
        && pdfPageContentCopy.table.widths
        && Array.isArray(pdfPageContentCopy.table.widths);
      const isTableBodyNotEmpty = !isEmpty(get(pdfPageContentCopy, 'table.body', false));

      if (isElemProperTable && isTableBodyNotEmpty) {
        pdfPageContentCopy = this.fixTableWidths(pdfPageContentCopy);
        pdfPageContentCopy = this.fixPdfTableContent(pdfPageContentCopy, keyWord, index);
      }

      if (isElementTable && !isTableBodyNotEmpty) {
        // element is broken <table> with empty body - add empty row to make it invisible (to look like on the preview)
        pdfPageContentCopy.table.body = [[]];
      }

      return pdfPageContentCopy;
    }, // getFixedPdfPageContent()

    /**
     * @param {Object} node
     * @return {string}
     */
    getTextFromNode(node) {
      let text = get(node, 'text', '');

      if (isArray(text)) {
        text = reduce(text, (acc, childNode) => {
          return acc + this.getTextFromNode(childNode);
        }, '');
      }

      return text && isString(text) ? text : '';
    },

    /**
     * Returns width and height of the provided image
     *
     * @param {string} src - image source
     * @returns {Promise} Promise object contains image and height of the image
     */
    getImageDimensions(src) {
      return getImageDimensionsUtil(src);
    }, // getImageDimensions()

    /**
     * Goes deep through the element structure and fixes all margins converting them from String to Number
     *
     * @param itemSrc original element
     * @returns Object -  modified element with fixed margins
     * */
    fixElementMargin(itemSrc) {
      return fixElementMarginUtil(itemSrc);
    }, // fixElementMargin()

    /**
     * Fixes paf table widths to make table columns auto-expandable.
     * Returns fixed element copy.
     *
     * @param {object} pdfPageContent
     * @return {object}
     */
    fixTableWidths(pdfPageContent) {
      let pdfPageContentCopy = cloneDeep(pdfPageContent);
      const { maxPageWidth, pagePaddingsX } = this.pdfMakePageSettings;
      const maxPageWidthWithoutPadding = maxPageWidth - pagePaddingsX;

      // star-sign width - makes the column auto-expandable taking all free space in a row
      const defaultColWidth = '*';
      const rowWithMostColumns = reduce(pdfPageContent.table.body, (rowWithMostCols, row) => (
        row.length > rowWithMostCols.length ? row : rowWithMostCols
      ), []);

      pdfPageContentCopy.table.widths = map(rowWithMostColumns, (col) => ((col.width < maxPageWidth)
        ? col.width
        : defaultColWidth));

      // total width presented in widths array without a wildcard value. Sometimes it can be larger that maxPageWidth
      // so we need to handle such case as well (e.g. widths=[150,*,420] in this case totalKnownWidth will be 570 and
      // such case should be treated as wide table as well)
      const totalKnownWidth = reduce(pdfPageContentCopy.table.widths, (knownWidth, width) => {
        if (width !== defaultColWidth) {
          return knownWidth + width;
        }
        return knownWidth;
      }, 0);

      const isPdfPageContentWiderThanMaxPageWidth = pdfPageContentCopy.width > maxPageWidthWithoutPadding;
      const isTotalKnownWidthBiggerThanMaxPageWidth = totalKnownWidth > maxPageWidthWithoutPadding;
      const isTableTooWide = isPdfPageContentWiderThanMaxPageWidth || isTotalKnownWidthBiggerThanMaxPageWidth;
      if (isTableTooWide) {
        pdfPageContentCopy = this.fixWideTableWidths(pdfPageContentCopy);
      }

      return pdfPageContentCopy;
    },

    /**
     * Fixes wide pdf table custom widths to fit page width.
     * Returns fixed element copy.
     *
     * @param {object} pdfPageContent
     * @return {object}
     */
    fixWideTableWidths(pdfPageContent) {
      const pdfPageContentCopy = cloneDeep(pdfPageContent);
      const { maxPageWidth, pagePaddingsX } = this.pdfMakePageSettings;
      const multiplier = (maxPageWidth - pagePaddingsX) / pdfPageContentCopy.width;

      pdfPageContentCopy.table.widths = map(
        pdfPageContentCopy.table.widths,
        (width) => (isNumber(width) ? Math.round(width * multiplier) : width),
      );
      pdfPageContentCopy.width = maxPageWidth;

      return pdfPageContentCopy;
    },

    /**
     * Fix table images width to fit table column width and returns a new pdf page content object.
     *
     * @param {object} pdfPageContent
     * @param {string} keyWord
     * @param {number} index
     * @return {object}
     */
    fixPdfTableContent(pdfPageContent, keyWord, index) {
      const pdfPageContentCopy = cloneDeep(pdfPageContent);
      const { table } = pdfPageContentCopy;

      const doesTableHasHeaderRow = !chain(pdfPageContentCopy.table.body)
        .head()
        .find((col) => col.nodeName === 'TH')
        .isNil()
        .value();
      const elementDefaultWidth = this.calcCellAutoWidth(table.widths);
      const tableBordersWidthSubtractionMultiplier = INITIAL_MULTIPLIER_VALUE
        - (WIDTH_OF_TABLE_BORDER * (table.widths.length - TABLE_COLS_DEFAULT_QUANTITY));

      pdfPageContentCopy.table.body = map(table.body, (row) => map(row, (originalCol, colIndex) => {
        const col = cloneDeep(originalCol);
        const colWidth = table.widths[colIndex];
        const doesColHasCustomWidth = isNumber(colWidth);
        const colWidthWithoutBorders = colWidth * tableBordersWidthSubtractionMultiplier;

        const mergedCellWidth = this.calcMergedCellWidth(col, table.widths, elementDefaultWidth, colIndex);
        const isColMerged = mergedCellWidth !== elementDefaultWidth;

        const imagesFinalWidth = (doesColHasCustomWidth && !isColMerged) ? colWidthWithoutBorders : mergedCellWidth;
        const otherElementsFinalWidth = doesColHasCustomWidth ? colWidthWithoutBorders : elementDefaultWidth;

        if (get(col, 'stack') && !isEmpty(col.stack)) {
          col.stack = map(col.stack, (originalElem) => {
            const element = this.getFixedPdfPageContent(originalElem, keyWord, index);
            element.minWidth = 0;
            element.maxWidth = otherElementsFinalWidth;

            if (element.nodeName !== 'IMG') {
              return element;
            }

            element.maxWidth = imagesFinalWidth;

            if (!get(pdfPageContentCopy, 'table.keepWithHeaderRows')) {
              pdfPageContentCopy.table.dontBreakRows = true;
              pdfPageContentCopy.table.headerRows = doesTableHasHeaderRow ? 1 : null;
              pdfPageContentCopy.table.keepWithHeaderRows = doesTableHasHeaderRow;
            }

            if (get(col, 'nodeName') === 'TH' && get(col, 'rowSpan', 0) > 1) {
              col.fillColor = '#000';
              col.fillOpacity = MERGED_HEADER_CELL_FILL_OPACITY;
            }

            return element;
          });
        }
        return col;
      }));

      pdfPageContentCopy.table.widths = map(table.widths, (width) => (width === '*'
        ? elementDefaultWidth
        : width * tableBordersWidthSubtractionMultiplier));

      return pdfPageContentCopy;
    }, // fixPdfTableContent()

    getRowWithMaximumColumnsNumber(tableRows) {
      let colsQuantity = 0;
      let rowWithMaximumColumnsNumber = '';

      forEach(tableRows, (row) => {
        const counter = (row.match(/<th.+?>|<td.+?>/gi) || []).length;
        if (counter > colsQuantity) {
          colsQuantity = counter;
          rowWithMaximumColumnsNumber = row;
        }
      });

      return { colsQuantity, rowWithMaximumColumnsNumber };
    },

    /**
     * Cleans empty table structures from htmlText. It is required because pdfmake drops with errors when trying to
     * export data with such html: <table style="width: 0px"><colgroup></colgroup><tbody><tr></tr></tbody></table>
     *
     * Note: This seems to be obsolete now - with the latest pdfmake version such tables seems to be ignored during
     * export - so this is not migrated to pdfMakeUtils.
     * @param {string} htmlText
     * @return {string}
     */
    removeEmptyTables(htmlText) {
      const tablePattern = /<table.+?>(.*?)<\/table>/gis;

      let updatedHtmlText = htmlText;
      let match;

      // eslint-disable-next-line no-cond-assign
      while ((match = tablePattern.exec(htmlText)) !== null) {
        const tableContent = match[1];
        /**
         * Trying to clean all tags and whitespaces that might be inside a table, if there is noting left after
         * cleaning that means that table was completely empty
         */
        const isTableEmpty = tableContent
          .replace(/<colgroup>.*?<\/colgroup>/gis, '') // Remove <colgroup> elements
          .replace(/<tr>\s*<td>\s*<\/td>\s*<\/tr>/gis, '') // Remove empty table rows
          .replace(/<(?!img\b)[^>]*>/g, '') // Remove all HTML tags except <img>
          .trim() === ''; // Check if the resulting string is empty

        if (isTableEmpty) {
          updatedHtmlText = updatedHtmlText.replace(match[0], '');
        }
      }
      return updatedHtmlText;
    }, // removeEmptyTables()

    /**
     * @param {string} templateText
     * @param {boolean} isPdfExporting
     * @return {string}
     */
    fixTemplateTables(templateText, isPdfExporting = false) {
      const colwidthPattern = /colwidth="(.+?)"/gi;
      const getColumnPattern = /<th.+?>|<td.+?>/gi;
      const searchTablePattern = /<table.+?>/gi;
      const hasTables = templateText.search(searchTablePattern) !== -1;
      // if we already have some colgroup than we will not use colgroupTag calculated below
      const hasColgroup = templateText.search(/<colgroup.+?>/gi) !== -1;
      const pdfFactor = 0.635; // multiplier we use to fit table in pdf page size

      if (!hasTables) {
        return templateText;
      }

      const templateTextWithoutEmptyTables = this.removeEmptyTables(templateText);
      const tables = templateTextWithoutEmptyTables.split('</table>');

      forEach(tables, (table, index) => {
        if (!table) {
          return;
        }

        const rows = table.split('</tr>');
        // getting a row with biggest columns number
        const { colsQuantity, rowWithMaximumColumnsNumber } = this.getRowWithMaximumColumnsNumber(rows);
        let tableWidth = 0;
        let colgroupTag = '<colgroup>';
        let colsWithWidth = 0;

        let match = getColumnPattern.exec(rowWithMaximumColumnsNumber);
        while (match) {
          const getColspanPattern = /colspan="(.+?)"/gi;
          const getColwidthPattern = /colwidth="(.+?)"/gi;
          const colspanMatch = getColspanPattern.exec(match[0]);
          const colwidthMatch = getColwidthPattern.exec(match[0]);
          const spanNumber = parseInt(colspanMatch?.[1] ?? 1, 10);
          const colWidth = parseInt(colwidthMatch?.[1] ?? 0, 10);
          const isMissingColumnWidth = colWidth === 0;

          // if there are some merged cells we should update colgroup with valid number of cols that table had initially
          const widths = colwidthMatch?.[1].split(',');
          // eslint-disable-next-line no-loop-func
          Array.from({ length: spanNumber }, (x, i) => {
            colgroupTag += widths?.[i] && widths?.[i] !== '0' ? `<col style="width: ${widths[i]}px;">` : '<col>';
            return i;
          });

          if (!isMissingColumnWidth) {
            tableWidth += colWidth;
            colsWithWidth += 1;
          }

          match = getColumnPattern.exec(rowWithMaximumColumnsNumber);
        }

        colgroupTag += '</colgroup>';

        if (isPdfExporting) {
          tableWidth *= pdfFactor;
        }

        const styleName = (colsWithWidth === colsQuantity) ? 'width' : 'min-width';
        tables[index] = tables[index]
          .replace(
            searchTablePattern,
            // eslint-disable-next-line max-len
            `<table style="${styleName}: ${Math.round(tableWidth)}px; border: 1px solid #e0e0e0; border-radius: unset; margin-bottom: 10px;">${hasColgroup ? '' : colgroupTag}`,
          );
      });

      return tables.join('</table>')
        .replace(colwidthPattern, (attr, value) => {
          if (includes(value, ',') || parseInt(value, 10) === 0) {
            return '';
          }

          return isPdfExporting
            ? `style="width: ${Math.round(value * pdfFactor)}px"`
            : `style="width: ${value}px"`;
        });
    }, // fixTemplateTables()

    removePdfTableBorders(stackItem) {
      return removePdfTableBordersUtil(stackItem);
    },

    /**
     * @param {string} html
     * @return {string}
     */
    alignTemplateImages(html) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const images = doc.querySelectorAll('img');
      images.forEach((image) => {
        const { textAlign } = image.style;

        if (textAlign) {
          switch (textAlign) {
            /* eslint-disable no-param-reassign */
            case 'left':
              image.style.margin = '0 auto 0 0';
              break;
            case 'center':
              image.style.margin = '0 auto';
              break;
            case 'right':
              image.style.margin = '0 0 0 auto';
              break;
            default:
              break;
            /* eslint-enable no-param-reassign */
          }
        }
      });

      return doc.body.innerHTML;
    }, // alignTemplateImages()

    /**
     * Download document to device's system Documents path and open it
     *
     * @param {Object} doc
     * @param {string} fileName
     */
    async downloadPdfOnNativeApp(doc, fileName) {
      const base64Doc = await getPdfBase64(doc);
      return this.downloadFileOnNativeApp(base64Doc, fileName);
    }, // downloadPdfOnNativeApp()

    /**
     * Download base64 file to device's system Documents path and open it
     *
     * @param {string} base64
     * @param {string} fileName
     */
    async downloadFileOnNativeApp(base64, fileName) {
      const fixedFileName = this.clearFileNameFromUnsupportedSymbols(fileName);
      const freeFileName = (this.isNativeAndroid && fileName.includes('xlsx'))
        ? await findFreeFileNameOnNativeApp(fixedFileName)
        : fixedFileName;
      const directory = (this.isNativeAndroid && fileName.includes('xlsx'))
        ? Directory.Documents
        : Directory.Data;

      const { uri } = await Filesystem.writeFile({
        path: freeFileName,
        data: base64,
        directory,
      });

      if (this.isNativeAndroid && fileName.includes('xlsx')) {
        return notifySuccess(this.$t('common.fileSavedIn', {
          fileName: freeFileName,
          path: 'Documents/Documents',
        }), {
          timeout: 10000,
        });
      }

      FileOpener.open({ filePath: uri });
    }, // downloadFileOnNativeApp()

    /**
     * @param {string} fileName
     */
    clearFileNameFromUnsupportedSymbols(fileName) {
      return trim(fileName.replace(/[:"|\\/?*<>]/g, '_'));
    },
  },
};
